import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import * as utils from 'src/app/shared/helpers/utils';
import { Router, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

export interface CanComponentDeactivate {
  canDeactivate: () => boolean;
  save?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesGuard implements CanDeactivate<CanComponentDeactivate> {
  private confirmationSubject = new Subject<boolean>();
  private currentComponent: CanComponentDeactivate | null = null;
  private nextStateUrl: string = ''; // Store destination URL

  constructor(private router: Router) {}

  canDeactivate(
    component: CanComponentDeactivate,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    if (!component.canDeactivate()) {
      this.currentComponent = component;
      this.nextStateUrl = nextState.url; // Store destination URL
      utils.toogleGenericModal('#confirmationModal-exit', false);
      return this.confirmationSubject.asObservable().pipe(take(1));
    }
    return true;
  }

  confirmNavigation() {
    this.currentComponent = null;
    this.nextStateUrl = '';
    this.confirmationSubject.next(true);
  }

  saveRefAndConfirmNavigation() {
    // Call save if the component has the method
    if (this.currentComponent && typeof this.currentComponent.save === 'function') {
      this.currentComponent.save();

      // Wait for save to complete, then handle navigation
      this.waitForSaveCompletion();
    } else {
      this.handleNavigation();
    }
  }

  private waitForSaveCompletion() {
    const checkInterval = setInterval(() => {
      // Check if the component still has unsaved changes
      if (this.currentComponent && 'referenceHasUnsavedChanges' in this.currentComponent) {
        const component = this.currentComponent as any;
        if (!component.referenceHasUnsavedChanges) {
          // Save completed
          clearInterval(checkInterval);
          this.handleNavigation();
        }
      } else {
        // Fallback if we can't monitor the state
        clearInterval(checkInterval);
        setTimeout(() => {
          this.handleNavigation();
        }, 500);
      }
    }, 100);

    // Safety timeout
    setTimeout(() => {
      clearInterval(checkInterval);
      this.handleNavigation();
    }, 5000);
  }

  private handleNavigation() {
    // Check if navigating to gaze-maps page
    if (this.nextStateUrl.includes('/gaze-maps')) {
      console.log('Navigating to gaze-maps after save, reloading page...');
      // Clear references first
      this.currentComponent = null;
      this.nextStateUrl = '';
      this.confirmationSubject.next(true);

      // Reload the page after navigation completes
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else {
      // Normal navigation
      this.currentComponent = null;
      this.nextStateUrl = '';
      this.confirmationSubject.next(true);
    }
  }
}